import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import LazyImage from '../ui/LazyImage';
import { Eye, Star, ArrowRight, ShieldCheck, Truck, Gift } from 'lucide-react';
import { warrantyByFrame } from '../../data/mock';

// Хелпер для прокрутки вверх
const scrollToTop = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  // Определяем гарантию по типу каркаса
  const getWarranty = () => {
    const frame = product.frame || '';
    if (frame.includes('40x20')) return warrantyByFrame['40x20'];
    if (frame.includes('25x25')) return warrantyByFrame['25x25'];
    return warrantyByFrame['20x20'];
  };

  const warranty = getWarranty();

  // Навигация на страницу товара с прокруткой вверх
  const handleProductClick = (e) => {
    e.preventDefault();
    scrollToTop();
    navigate(`/product/${product.slug}`);
  };

  // УТП по категории
  const getUSP = () => {
    const category = product.category;
    switch(category) {
      case 'standart':
        return { label: 'Выгодная цена', color: 'bg-blue-100 text-blue-700' };
      case 'standart-usilennaya':
        return { label: 'Усиленный каркас', color: 'bg-purple-100 text-purple-700' };
      case 'bogatyr':
        return { label: 'Повышенная прочность', color: 'bg-orange-100 text-orange-700' };
      case 'premium':
        return { label: 'Премиум качество', color: 'bg-amber-100 text-amber-700' };
      case 'domikom':
        return { label: 'Прямые стены', color: 'bg-emerald-100 text-emerald-700' };
      default:
        return null;
    }
  };

  const usp = getUSP();

  return (
    <div className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:border-emerald-200 transition-all duration-300">
      {/* Image with Lazy Loading */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <LazyImage
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full group-hover:scale-105 transition-transform duration-500"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.isHit && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white font-semibold">
              Хит продаж
            </Badge>
          )}
          {product.isNew && (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-semibold">
              Новинка
            </Badge>
          )}
          {usp && (
            <Badge className={`${usp.color} font-medium`}>
              {usp.label}
            </Badge>
          )}
        </div>

        {/* Quick view button */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Link to={`/product/${product.slug}`} onClick={handleProductClick}>
            <Button variant="secondary" size="sm" className="bg-white hover:bg-gray-100">
              <Eye className="w-4 h-4 mr-2" />
              Подробнее
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Category & Rating */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-emerald-600 font-medium uppercase tracking-wide">
            {product.size.width}x{product.size.length} м
          </span>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm text-gray-600">5.0</span>
          </div>
        </div>

        {/* Title */}
        <Link to={`/product/${product.slug}`} onClick={handleProductClick}>
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
            {product.name}
          </h3>
        </Link>

        {/* Features - УТП */}
        <ul className="text-sm text-gray-600 mb-3 space-y-1">
          <li className="flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span>Гарантия {warranty} {warranty === 1 ? 'год' : warranty < 5 ? 'года' : 'лет'}<sup className="text-emerald-600">*</sup></span>
          </li>
          <li className="flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span>Бесплатная доставка</span>
          </li>
          <li className="flex items-center gap-1.5">
            <Gift className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span>Форточки в комплекте</span>
          </li>
        </ul>

        {/* Frame info */}
        <p className="text-xs text-gray-500 mb-3">
          Каркас: {product.frame?.split(' ').slice(0, 3).join(' ')} • Шаг: {product.step || '65 см'}
        </p>

        {/* Price */}
        <div className="flex items-end gap-2 mb-3">
          <span className="text-2xl font-bold text-emerald-700">
            {formatPrice(product.price)} ₽
          </span>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <Link to={`/product/${product.slug}`} onClick={handleProductClick} className="flex-1">
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`product-details-btn-${product.id}`}
            >
              Подробнее
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Stock status */}
        <p className={`text-sm mt-3 font-medium ${product.inStock ? 'text-emerald-600' : 'text-red-500'}`}>
          {product.inStock ? '✓ В наличии' : 'Под заказ'}
        </p>
      </div>
    </div>
  );
};

export default ProductCard;
